/** @format */

import { Brand, ParentChildrenResponse, compareQuestionNumbers, flattenTree } from "@question-bank/shared";
import { useEffect, useState, useCallback, useMemo } from "react";
import { getQuestionChildren } from "../api/questions";
import { useCart } from "../contexts/cart/useCart";
import { PaperEntry } from "../utils/paper";
import { QuestionNode } from "../utils/helpers";
export interface PaperGroup {
	entry: PaperEntry;
	questions: QuestionNode[];
}
interface UsePaperQuestionsReturn {
	groups: PaperGroup[];
	isLoading: boolean;
	error: string | null;
	entryCount: number;
	reload: () => void;
}

const cacheKey = (brand: Brand, parentQuestionId: string): string => `${brand}:${parentQuestionId}`;

const buildGroups = (results: ParentChildrenResponse[], entries: PaperEntry[]): PaperGroup[] =>
	results.map((result, index) => {
		const entry = entries[index];
		const sorted = flattenTree(result.items).sort((a, b) => compareQuestionNumbers(a.questionNumber, b.questionNumber));

		const questions = entry.selectedParts
			? sorted.filter((q) => entry.selectedParts!.some((p) => p.id === q.id))
			: sorted;
		return { entry, questions };
	});

export const usePaperQuestions = (brand: Brand): UsePaperQuestionsReturn => {
	const [responses, setResponses] = useState<Record<string, ParentChildrenResponse>>({});
	const [error, setError] = useState<string | null>(null);

	const { addedQuestions } = useCart();
	const entryCount = addedQuestions.length;

	const missingIds = useMemo(
		() =>
			addedQuestions
				.map((entry) => entry.parentQuestionId)
				.filter((buildParentQuestionId) => !(cacheKey(brand, buildParentQuestionId) in responses)),
		[addedQuestions, brand, responses],
	);
	const isLoading = missingIds.length > 0 && error === null;
	useEffect(() => {
		if (missingIds.length === 0) {
			return;
		}
		let isCancelled = false;
		setError(null);

		Promise.all(missingIds.map((buildParentQuestionId) => getQuestionChildren(brand, buildParentQuestionId)))
			.then((results) => {
				if (isCancelled) {
					return;
				}
				setResponses((prev) => {
					const next = { ...prev };
					results.forEach((result, index) => {
						next[cacheKey(brand, missingIds[index])] = result;
					});
					return next;
				});
			})
			.catch((err) => {
				if (!isCancelled) {
					setError(err instanceof Error ? err.message : "Failed to load question. Please try again");
				}
			});

		return () => {
			isCancelled = true;
		};
	}, [brand, missingIds]);

	const reload = useCallback(() => {
		setResponses({});
		setError(null);
	}, []);

	const groups = useMemo(
		() =>
			missingIds.length > 0
				? []
				: buildGroups(
						addedQuestions.map((entry) => responses[cacheKey(brand, entry.parentQuestionId)]),
						addedQuestions,
					),
		[addedQuestions, brand, missingIds, responses],
	);

	return { groups, isLoading, error, entryCount, reload };
};
